import AbstractAction from "./AbstractAction.js";
import OrderedMenuItem from "../OrderedMenuItem.js";

/**
 * A spec used to create a ProseMirror node.
 *
 * @typedef {object} NodeSpec
 * @property {string} type - Type of the node as defined in the ProseMirror schema.
 *   It can also be "html" to insert raw HTML text directly
 * @property {function} content - HTML content used when the `type` is "html"
 * @property {NodeSpec} attributes - Node attributes
 */

/**
 * An item of the InputValueDropdownAction.
 *
 * @typedef {object} InputValueDropdownInput
 * @property {Renderable} label - Text for the item to display
 * @property {function(string):boolean} match - Function returning `true` when this item is a match
 *   during a filter. This method takes a string as parameter that is the user search input
 * @property {NodeSpec} nodeSpec - Spec used to create a ProseMirror node
 */

/**
 * `InputValueDropdownAction` is a basic action to compose a
 * dropdown menubar item for ProseMirror's editor.
 *
 * @param {string} label - Content of the button opening the list
 * @param {InputValueDropdownInput[]|Promise} items - Content of
 *   the dropdown list. This parameter accepts a list of items or
 *   a Promise, resolving to a list of items
 * @param {number} [order] - Order of the MenuItem. The bigger the
 *   number, the more on the right-hand side the menu item will be
 * @param {string} dataTestName - A string to be used to find the
 *   dropdown button within the test framework
 */
export default class InputValueDropdownAction extends AbstractAction {
	constructor({ label, items, order, dataTestName = "" }) {
		super(...arguments);

		this._label = label;
		this._items = Promise.resolve(items);
		this._order = order;
		this._dataTestName = dataTestName;
	}

	getMenuItems({ actionsManager }) {
		return [
			new OrderedMenuItem({
				order: this._order,
				menuItem: this._makeDropdownMenuItem({
					items: this._adaptItemsToDropdown({
						actionsManager,
					}),
					label: this._label.toString(),
					dataTestName: this._dataTestName,
				}),
			}),
		];
	}

	async _adaptItemsToDropdown({ actionsManager }) {
		const items = await this._items;

		return items.map((item) =>
			this._adaptItemToDropdown({ item, actionsManager }),
		);
	}

	_adaptItemToDropdown({ item, actionsManager }) {
		return {
			label: item.label.toString(),
			match: (string) => item.match(string),
			run: actionsManager.replaceSelectionWithInputValue(item.nodeSpec),
		};
	}
}
